import React, { useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  IconButton,
  Stack,
  Chip,
  Grid,
  alpha,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add,
  Remove,
  ShoppingCart,
  Close,
  Restaurant,
  Spa,
  LocalFireDepartment,
  CheckCircle,
  Tune,
  RestaurantMenu,
  ArrowForward,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import ImageLoader from "components/ImageLoader/ImageLoader";
import { formatCurrency } from "../../../utils/currencyUtils";
import { LUXURY, getMenuCartChrome } from "data/guestLuxuryTheme";

const AddToCartDialog = ({
  luxury = false,
  open,
  onClose,
  item,
  onAddToCart,
  onCompleteOrder,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const chrome = getMenuCartChrome(luxury);
  const accent = luxury ? LUXURY.orange : theme.palette.primary.main;
  const accentDark = luxury ? LUXURY.orangeDark : theme.palette.primary.dark;
  const secondary = luxury ? LUXURY.gold : theme.palette.secondary.main;
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedModifiers, setSelectedModifiers] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [postAddChoiceOpen, setPostAddChoiceOpen] = useState(false);

  const hasVariations = item?.ItemVariations && item.ItemVariations.length > 0;
  const hasModifiers = item?.ModifierOptions && item.ModifierOptions.length > 0;

  // Sort variations by display order and calculate lowest price
  const sortedVariations = React.useMemo(() => {
    if (!hasVariations) return [];

    return [...item.ItemVariations].sort((a, b) => {
      const orderA = a.displayOrder || 0;
      const orderB = b.displayOrder || 0;
      return orderA - orderB;
    });
  }, [item?.ItemVariations, hasVariations]);

  // Calculate lowest variation price to show as initial base price
  const lowestVariationPrice = React.useMemo(() => {
    if (!hasVariations) return 0;

    const prices = sortedVariations.map((v) => {
      const price = String(v.price || "0").replace(/[^0-9.-]/g, "");
      return parseFloat(price) || 0;
    });

    return Math.min(...prices);
  }, [sortedVariations, hasVariations]);

  // Set default variation if item has no base price but has variations
  React.useEffect(() => {
    if (open && item && hasVariations && sortedVariations.length > 0) {
      // Check if item has no base price or base price is 0
      const itemBasePrice =
        parseFloat(String(item.basePrice || "0").replace(/[^0-9.-]/g, "")) || 0;

      // Auto-select first variation if base price is 0
      if (itemBasePrice === 0 && !selectedVariation) {
        const firstVariation = sortedVariations[0];
        setSelectedVariation(firstVariation);
      }
    }
  }, [
    open,
    item,
    hasVariations,
    selectedVariation,
    sortedVariations,
    lowestVariationPrice,
  ]);

  // Group modifier options by their parent Modifier
  const groupedModifiers = React.useMemo(() => {
    if (!item?.ModifierOptions) return [];

    const groups = {};
    item.ModifierOptions.forEach((option) => {
      const modifierId = option.Modifier.id;
      if (!groups[modifierId]) {
        groups[modifierId] = {
          modifier: option.Modifier,
          options: [],
        };
      }
      groups[modifierId].options.push(option);
    });

    return Object.values(groups);
  }, [item?.ModifierOptions]);

  // Calculate price with proper string cleaning and debugging
  const basePrice = React.useMemo(() => {
    let price = 0;

    if (selectedVariation) {
      // Use selected variation price
      const varPrice = String(selectedVariation.price || "0").replace(
        /[^0-9.-]/g,
        "",
      );
      price = parseFloat(varPrice) || 0;
    } else if (hasVariations) {
      // If has variations but none selected, show lowest variation price
      price = lowestVariationPrice;
    } else {
      // Use item base price
      const itemPrice = String(item?.basePrice || "0").replace(/[^0-9.-]/g, "");
      price = parseFloat(itemPrice) || 0;
    }

    return price;
  }, [selectedVariation, item?.basePrice, hasVariations, lowestVariationPrice]);

  const modifiersPrice = selectedModifiers.reduce((sum, mod) => {
    const modPrice =
      parseFloat(String(mod.price || "0").replace(/[^0-9.-]/g, "")) || 0;
    return sum + modPrice;
  }, 0);

  const totalPrice = (basePrice + modifiersPrice) * quantity;

  // Check if all required modifiers are fulfilled
  const areRequiredModifiersFulfilled = React.useMemo(() => {
    for (const group of groupedModifiers) {
      if (group.modifier.isRequired) {
        const selectedCount = selectedModifiers.filter(
          (m) => m.modifierId === group.modifier.id,
        ).length;
        if (selectedCount < group.modifier.minSelections) {
          return false;
        }
      }
    }
    return true;
  }, [groupedModifiers, selectedModifiers]);

  // Check if button should be disabled
  const isAddToCartDisabled =
    (hasVariations && !selectedVariation) || !areRequiredModifiersFulfilled;

  const resetSelections = () => {
    setSelectedVariation(null);
    setSelectedModifiers([]);
    setQuantity(1);
  };

  const handleModifierToggle = (modifierOption, modifier) => {
    setSelectedModifiers((prev) => {
      const modifierGroupOptions = prev.filter(
        (m) => m.modifierId === modifier.id,
      );
      const exists = prev.find((m) => m.id === modifierOption.id);

      // If maxSelections is null, treat as 1 (single-select per category)
      const effectiveMax = modifier.maxSelections ?? 1;

      if (effectiveMax === 1) {
        // Radio-style: toggle off if same, otherwise replace selection for this group
        if (exists) {
          return prev.filter((m) => m.id !== modifierOption.id);
        }
        return [
          ...prev.filter((m) => m.modifierId !== modifier.id),
          modifierOption,
        ];
      }

      // Checkbox behavior - toggle selection
      if (exists) {
        return prev.filter((m) => m.id !== modifierOption.id);
      } else {
        // Check max selections
        if (modifierGroupOptions.length >= effectiveMax) {
          return prev; // Don't add if max reached
        }
        return [...prev, modifierOption];
      }
    });
  };

  const handleAddToCart = () => {
    // Already validated by disabled state, but keep as safety check
    if (hasVariations && !selectedVariation) {
      toast.warning("Please select a size");
      return;
    }

    // Validate required modifiers
    for (const group of groupedModifiers) {
      if (group.modifier.isRequired) {
        const selectedCount = selectedModifiers.filter(
          (m) => m.modifierId === group.modifier.id,
        ).length;
        if (selectedCount < group.modifier.minSelections) {
          toast.warning(
            `Please select at least ${group.modifier.minSelections} option(s) for ${group.modifier.name}`,
          );
          return;
        }
      }
    }

    const cartItem = {
      menuItem: item,
      variation: selectedVariation,
      modifiers: selectedModifiers,
      quantity,
    };

    onAddToCart(cartItem);
    // Ask user what to do next after adding one item
    setPostAddChoiceOpen(true);
  };

  const handleAddAnotherVariation = () => {
    resetSelections();
    setPostAddChoiceOpen(false);
  };

  const handleAddNewItem = () => {
    setPostAddChoiceOpen(false);
    handleClose();
  };

  const handleCompleteOrder = () => {
    setPostAddChoiceOpen(false);
    handleClose();
    if (typeof onCompleteOrder === "function") {
      onCompleteOrder();
    }
  };

  const handleClose = () => {
    resetSelections();
    setPostAddChoiceOpen(false);
    onClose();
  };

  const sectionCardSx = {
    mt: 1.7,
    p: { xs: 1.35, md: 1.7 },
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
    backgroundColor: alpha(theme.palette.background.paper, 0.9),
    boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)",
  };

  if (!item) return null;

  const dialogSize = hasVariations || hasModifiers ? "xl" : "sm";

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={dialogSize}
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2.5,
            border: isMobile ? "none" : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            boxShadow: isMobile ? "none" : "0 24px 54px rgba(15, 23, 42, 0.25)",
            overflow: "hidden",
            maxHeight: isMobile ? "100vh" : "92vh",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(7, 14, 28, 0.46)",
            backdropFilter: "blur(7px)",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            position: "relative",
            overflow: "hidden",
            background: "#0f172a",
          }}
        >
          <Box
            sx={{
              position: "relative",
              minHeight: { xs: 152, md: 220 },
              background: `linear-gradient(140deg, ${accentDark} 0%, ${accent} 52%, ${secondary} 100%)`,
            }}
          >
            {item.image ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0.28,
                }}
              >
                <ImageLoader
                  imagePath={item.image}
                  alt={item.name}
                  width="100%"
                  height="100%"
                  rounded={false}
                />
              </Box>
            ) : null}

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(15,23,42,0.15) 0%, rgba(15,23,42,0.78) 100%)",
              }}
            />

            <IconButton
              onClick={handleClose}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                bgcolor: alpha("#ffffff", 0.16),
                color: "#ffffff",
                backdropFilter: "blur(8px)",
                border: `1px solid ${alpha("#ffffff", 0.18)}`,
                zIndex: 2,
                "&:hover": {
                  bgcolor: alpha("#ffffff", 0.24),
                },
              }}
            >
              <Close />
            </IconButton>

            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                minHeight: { xs: 152, md: 220 },
                p: { xs: 1.5, md: 3 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap", pr: 6 }}>
                <Chip
                  label="Customize item"
                  size="small"
                  sx={{
                    bgcolor: alpha("#ffffff", 0.16),
                    color: "#ffffff",
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    height: 24,
                  }}
                />
                {hasVariations && (
                  <Chip
                    label={`${sortedVariations.length} size option${sortedVariations.length > 1 ? "s" : ""}`}
                    size="small"
                    sx={{
                      bgcolor: alpha("#ffffff", 0.12),
                      color: alpha("#ffffff", 0.92),
                      height: 24,
                      display: { xs: "none", md: "inline-flex" },
                    }}
                  />
                )}
                {hasModifiers && (
                  <Chip
                    label={`${groupedModifiers.length} add-on group${groupedModifiers.length > 1 ? "s" : ""}`}
                    size="small"
                    sx={{
                      bgcolor: alpha("#ffffff", 0.12),
                      color: alpha("#ffffff", 0.92),
                      height: 24,
                      display: { xs: "none", md: "inline-flex" },
                    }}
                  />
                )}
              </Box>

              <Box sx={{ maxWidth: 620 }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#ffffff",
                    fontWeight: 800,
                    lineHeight: 1.1,
                    fontSize: { xs: "1.18rem", md: "2.2rem" },
                    mb: { xs: 0.4, md: 1 },
                  }}
                >
                  {item.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha("#ffffff", 0.86),
                    maxWidth: 560,
                    lineHeight: 1.6,
                    fontSize: { xs: "0.78rem", md: "0.98rem" },
                    display: "-webkit-box",
                    WebkitLineClamp: { xs: 1, md: 2 },
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    mb: { xs: 1, md: 2 },
                  }}
                >
                  {item.description || "Adjust your size, extras, and quantity before adding this item to your order."}
                </Typography>

                <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                  <Box
                    sx={{
                      px: { xs: 1.1, md: 1.4 },
                      py: { xs: 0.75, md: 0.95 },
                      borderRadius: 1.5,
                      bgcolor: alpha("#ffffff", 0.12),
                      backdropFilter: "blur(8px)",
                      border: `1px solid ${alpha("#ffffff", 0.18)}`,
                      minWidth: { xs: 120, md: 142 },
                      boxShadow: "0 10px 24px rgba(15, 23, 42, 0.14)",
                    }}
                  >
                    <Typography sx={{ color: alpha("#ffffff", 0.7), fontSize: { xs: "0.6rem", md: "0.68rem" }, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.55, mb: 0.25 }}>
                      Starting from
                    </Typography>
                    <Typography sx={{ color: "#ffffff", fontSize: { xs: "1rem", md: "1.18rem" }, fontWeight: 900, lineHeight: 1.1 }}>
                      {formatCurrency(basePrice)}
                    </Typography>
                  </Box>
                  {!!item?.isVegetarian && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 1.05,
                        py: 0.62,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.success.light, 0.18),
                        border: `1px solid ${alpha(theme.palette.success.light, 0.32)}`,
                        backdropFilter: "blur(8px)",
                        boxShadow: `0 10px 20px ${alpha(theme.palette.success.dark, 0.14)}`,
                      }}
                    >
                      <Spa sx={{ fontSize: 15, color: theme.palette.success.light }} />
                      <Typography sx={{ color: "#f8fafc", fontSize: "0.78rem", fontWeight: 800, letterSpacing: 0.15, lineHeight: 1 }}>
                        Veg
                      </Typography>
                    </Box>
                  )}
                  {!!item?.isVegan && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 1.05,
                        py: 0.62,
                        borderRadius: 1.5,
                        bgcolor: alpha("#86efac", 0.16),
                        border: `1px solid ${alpha("#86efac", 0.3)}`,
                        backdropFilter: "blur(8px)",
                        boxShadow: `0 10px 20px ${alpha("#14532d", 0.16)}`,
                      }}
                    >
                      <Spa sx={{ fontSize: 15, color: "#86efac" }} />
                      <Typography sx={{ color: "#f8fafc", fontSize: "0.78rem", fontWeight: 800, letterSpacing: 0.15, lineHeight: 1 }}>
                        Vegan
                      </Typography>
                    </Box>
                  )}
                  {!!item?.isSpicy && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 1.05,
                        py: 0.62,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.error.light, 0.16),
                        border: `1px solid ${alpha(theme.palette.error.light, 0.28)}`,
                        backdropFilter: "blur(8px)",
                        boxShadow: `0 10px 20px ${alpha(theme.palette.error.dark, 0.16)}`,
                      }}
                    >
                      <LocalFireDepartment sx={{ fontSize: 15, color: theme.palette.error.light }} />
                      <Typography sx={{ color: "#f8fafc", fontSize: "0.78rem", fontWeight: 800, letterSpacing: 0.15, lineHeight: 1 }}>
                        Spicy
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: { xs: 2, md: 3 },
            pb: { xs: 16, md: 3 },
            background: `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.86)} 100%)`,
          }}
        >
          {/* ── Simple layout for items with no customisation ── */}
          {!hasVariations && !hasModifiers && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                py: 1,
              }}
            >
              {/* Item image */}
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 260,
                  aspectRatio: "4 / 3",
                  borderRadius: 2.5,
                  overflow: "hidden",
                  bgcolor: alpha(theme.palette.text.secondary, 0.06),
                  boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`,
                  flexShrink: 0,
                }}
              >
                {item.image ? (
                  <ImageLoader
                    imagePath={item.image}
                    alt={item.name}
                    width="100%"
                    height="100%"
                    rounded={false}
                  />
                ) : (
                  <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Restaurant sx={{ fontSize: 54, color: alpha(theme.palette.text.primary, 0.18) }} />
                  </Box>
                )}
              </Box>

              {/* Price + ready label */}
              <Box sx={{ textAlign: "center" }}>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, color: "#94a3b8", mb: 0.5 }}>
                  Price
                </Typography>
                <Typography sx={{ fontSize: "2rem", fontWeight: 900, color: accent, lineHeight: 1 }}>
                  {formatCurrency(basePrice, "Rs.", { space: true })}
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: "#64748b", mt: 0.75 }}>
                  Use the quantity controls below to adjust amount.
                </Typography>
              </Box>
            </Box>
          )}

          {/* ── Full customisation grid for items with variations / modifiers ── */}
          {(hasVariations || hasModifiers) && (
            <Grid container spacing={{ xs: 2, md: 2.25 }}>
              <Grid size={{ xs: 12, md: 3.6 }} sx={{ order: { xs: 2, md: 1 } }}>
                <Stack spacing={2} sx={{ position: { md: "sticky" }, top: { md: 15 } }}>
                  <Box sx={sectionCardSx}>
                    <Box
                      sx={{
                        position: "relative",
                        height: { xs: 140, md: 240 },
                        borderRadius: 1.5,
                        overflow: "hidden",
                        bgcolor: alpha(theme.palette.text.secondary, 0.05),
                        mb: 1.5,
                        display: { xs: "none", md: "block" },
                      }}
                    >
                      {item.image ? (
                        <ImageLoader
                          imagePath={item.image}
                          alt={item.name}
                          width="100%"
                          height="100%"
                          rounded={false}
                        />
                      ) : (
                        <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Restaurant sx={{ fontSize: 54, color: alpha(theme.palette.text.primary, 0.18) }} />
                        </Box>
                      )}
                    </Box>

                    <Typography sx={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 0.45, color: "#64748b", fontWeight: 700, mb: 0.75 }}>
                      Your Selection
                    </Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                        <Typography sx={{ color: "#475569", fontSize: "0.92rem" }}>Base item</Typography>
                        <Typography sx={{ color: "#0f172a", fontWeight: 700, fontSize: "0.92rem" }}>{formatCurrency(basePrice)}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                        <Typography sx={{ color: "#475569", fontSize: "0.92rem" }}>Add-ons</Typography>
                        <Typography sx={{ color: "#0f172a", fontWeight: 700, fontSize: "0.92rem" }}>
                          {selectedModifiers.length ? `${selectedModifiers.length} selected` : "None"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                        <Typography sx={{ color: "#475569", fontSize: "0.92rem" }}>Extras total</Typography>
                        <Typography sx={{ color: "#0f172a", fontWeight: 700, fontSize: "0.92rem" }}>{formatCurrency(modifiersPrice)}</Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {selectedVariation ? (
                        <Chip label={`Size: ${selectedVariation.name}`} sx={{ fontWeight: 700 }} />
                      ) : hasVariations ? (
                        <Chip label="Choose a size" sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.warning.main, 0.12), color: theme.palette.warning.dark }} />
                      ) : null}
                      {selectedModifiers.length > 0 ? (
                        <Chip label={`${selectedModifiers.length} extras added`} sx={{ fontWeight: 700, bgcolor: alpha(accent, 0.1), color: accent }} />
                      ) : null}
                    </Stack>
                  </Box>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 8.4 }} sx={{ order: { xs: 1, md: 2 } }}>
                <Stack spacing={2}>
                  <Box
                    sx={{
                      ...sectionCardSx,
                      display: { xs: "block", md: "none" },
                      p: 1.3,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                      <Box>
                        <Typography sx={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 0.4, color: "#64748b", fontWeight: 700 }}>
                          Current total
                        </Typography>
                        <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, color: accent, mt: 0.3 }}>
                          {formatCurrency(totalPrice)}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                        {selectedVariation ? (
                          <Chip label={selectedVariation.name} size="small" sx={{ fontWeight: 700 }} />
                        ) : hasVariations ? (
                          <Chip label="Select size" size="small" sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.warning.main, 0.12), color: theme.palette.warning.dark }} />
                        ) : null}
                        {selectedModifiers.length > 0 ? (
                          <Chip label={`${selectedModifiers.length} extras`} size="small" sx={{ fontWeight: 700, bgcolor: alpha(accent, 0.1), color: accent }} />
                        ) : null}
                      </Stack>
                    </Box>
                  </Box>

                  {hasVariations && (
                    <Box sx={sectionCardSx}>
                      <Box sx={{ display: "flex", alignItems: "start", justifyContent: "space-between", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
                        <Box>
                          <Typography sx={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: 0.45, color: "#64748b", fontWeight: 700, mb: 0.4 }}>
                            Step 1
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", fontSize: { xs: "1.02rem", md: "1.12rem" } }}>
                            Pick your size
                          </Typography>
                          <Typography sx={{ color: "#64748b", fontSize: "0.9rem", mt: 0.4 }}>
                            Start with the portion that fits the order best.
                          </Typography>
                        </Box>
                        <Chip label="Required" sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }} />
                      </Box>

                      <RadioGroup
                        value={selectedVariation?.id || ""}
                        onChange={(e) => {
                          const variation = sortedVariations.find(
                            (v) => v.id === e.target.value,
                          );
                          setSelectedVariation(variation);
                        }}
                      >
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "repeat(2, minmax(0, 1fr))",
                              lg: "repeat(3, minmax(0, 1fr))",
                            },
                            gap: 1.2,
                          }}
                        >
                          {sortedVariations.map((variation) => {
                            const selected = selectedVariation?.id === variation.id;
                            const variationPrice =
                              parseFloat(String(variation.price || "0").replace(/[^0-9.-]/g, "")) || 0;

                            return (
                              <Box
                                key={variation.id}
                                onClick={() => setSelectedVariation(variation)}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "stretch",
                                  justifyContent: "space-between",
                                  gap: 1,
                                  p: 1.5,
                                  borderRadius: 1.5,
                                  cursor: "pointer",
                                  border: `1px solid ${selected ? accent : alpha(theme.palette.divider, 0.6)}`,
                                  backgroundColor: selected ? alpha(accent, 0.06) : alpha(theme.palette.background.default, 0.45),
                                  boxShadow: selected ? `0 10px 22px ${alpha(accent, 0.14)}` : "none",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    borderColor: accent,
                                    backgroundColor: alpha(accent, 0.04),
                                  },
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                                  <FormControlLabel
                                    value={variation.id}
                                    control={<Radio size={isMobile ? "medium" : "small"} checked={selected} />}
                                    label={
                                      <Box>
                                        <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.95rem", lineHeight: 1.2 }}>
                                          {variation.name}
                                        </Typography>
                                        <Typography sx={{ color: "#64748b", fontSize: "0.79rem", mt: 0.35 }}>
                                          {selected ? "Selected" : "Tap to choose"}
                                        </Typography>
                                      </Box>
                                    }
                                    sx={{ m: 0, alignItems: "flex-start", flex: 1 }}
                                  />
                                </Box>
                                <Typography sx={{ fontWeight: 800, color: accent, fontSize: "0.95rem", pl: 0.6 }}>
                                  {formatCurrency(variationPrice)}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </RadioGroup>
                    </Box>
                  )}

                  {hasModifiers && (
                    <Box sx={sectionCardSx}>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography sx={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: 0.45, color: "#64748b", fontWeight: 700, mb: 0.4 }}>
                          {hasVariations ? "Step 2" : "Step 1"}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", fontSize: { xs: "1.02rem", md: "1.12rem" } }}>
                          Customize your item
                        </Typography>
                        <Typography sx={{ color: "#64748b", fontSize: "0.9rem", mt: 0.4 }}>
                          Add the extras your customer wants. Required groups are marked clearly.
                        </Typography>
                      </Box>

                      <Stack spacing={1.5}>
                        {groupedModifiers.map((group) => (
                          <Box
                            key={group.modifier.id}
                            sx={{
                              p: 1.4,
                              borderRadius: 1.75,
                              border: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
                              backgroundColor: alpha(theme.palette.background.default, 0.45),
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.2, mb: 1, flexWrap: "wrap" }}>
                              <Box>
                                <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.95rem" }}>
                                  {group.modifier.name}
                                </Typography>
                                {group.modifier.description ? (
                                  <Typography sx={{ color: "#64748b", fontSize: "0.82rem", mt: 0.35 }}>
                                    {group.modifier.description}
                                  </Typography>
                                ) : null}
                              </Box>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {group.modifier.isRequired && (
                                  <Chip label="Required" size="small" sx={{ bgcolor: alpha(theme.palette.error.main, 0.12), color: theme.palette.error.main, fontWeight: 700 }} />
                                )}
                                <Chip
                                  label={group.modifier.maxSelections ? `Up to ${group.modifier.maxSelections}` : "Choose 1"}
                                  size="small"
                                  sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main, fontWeight: 700 }}
                                />
                              </Stack>
                            </Box>

                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "1fr",
                                  sm: "repeat(2, minmax(0, 1fr))",
                                },
                                gap: 1,
                              }}
                            >
                              {group.options.map((option) => {
                                const checked = !!selectedModifiers.find((m) => m.id === option.id);
                                const optionPrice = parseFloat(String(option.price || "0").replace(/[^0-9.-]/g, "")) || 0;

                                return (
                                  <Box
                                    key={option.id}
                                    onClick={() => handleModifierToggle(option, group.modifier)}
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "stretch",
                                      justifyContent: "space-between",
                                      gap: 0.8,
                                      p: 1.25,
                                      borderRadius: 2,
                                      cursor: "pointer",
                                      border: `1px solid ${checked ? accent : alpha(theme.palette.divider, 0.45)}`,
                                      backgroundColor: checked ? alpha(accent, 0.06) : theme.palette.background.paper,
                                      boxShadow: checked ? `0 8px 18px ${alpha(accent, 0.12)}` : "none",
                                      transition: "all 0.2s ease",
                                      "&:hover": {
                                        borderColor: accent,
                                        backgroundColor: alpha(accent, 0.04),
                                      },
                                    }}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          size={isMobile ? "medium" : "small"}
                                          checked={checked}
                                          onChange={() => handleModifierToggle(option, group.modifier)}
                                        />
                                      }
                                      label={
                                        <Box>
                                          <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.9rem", lineHeight: 1.2 }}>
                                            {option.name}
                                          </Typography>
                                          <Typography sx={{ color: "#64748b", fontSize: "0.78rem", mt: 0.35 }}>
                                            {checked ? "Added" : "Tap to add"}
                                          </Typography>
                                        </Box>
                                      }
                                      sx={{ m: 0, alignItems: "flex-start", flex: 1 }}
                                    />
                                    <Typography sx={{ fontWeight: 800, color: optionPrice > 0 ? accent : "#64748b", fontSize: "0.88rem", pl: 0.6 }}>
                                      {optionPrice > 0 ? `+${formatCurrency(optionPrice)}` : "Included"}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Grid>
            </Grid>
          )} {/* end hasVariations || hasModifiers */}
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: { xs: 1.75, md: 2.25 },
            pb: { xs: "calc(env(safe-area-inset-bottom, 0px) + 12px)", md: 2.25 },
            backgroundColor: "#ffffff",
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 1.25, md: 0 },
            alignItems: "stretch",
          }}
        >
          {/* Qty + Price row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 2, md: 3 },
              flex: 1,
            }}
          >
            {/* Qty stepper */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
              <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5, textTransform: "uppercase" }}>
                Qty
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 1.5,
                  border: `1.5px solid ${alpha(theme.palette.divider, 0.5)}`,
                  bgcolor: "#f8fafc",
                  overflow: "hidden",
                  height: 38,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  sx={{ borderRadius: 0, px: 1.2, height: "100%", "&:disabled": { opacity: 0.35 } }}
                >
                  <Remove sx={{ fontSize: 16 }} />
                </IconButton>
                <Typography sx={{ minWidth: 36, textAlign: "center", fontWeight: 800, fontSize: "0.95rem", color: "#0f172a" }}>
                  {quantity}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setQuantity(quantity + 1)}
                  sx={{ borderRadius: 0, px: 1.2, height: "100%" }}
                >
                  <Add sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>

            {/* Total price */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
              <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5, textTransform: "uppercase" }}>
                Total
              </Typography>
              <Typography sx={{ fontSize: "1.3rem", fontWeight: 900, color: accent, lineHeight: 1.1 }}>
                {formatCurrency(totalPrice, "Rs.", { space: true })}
              </Typography>
            </Box>
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0, width: { xs: "100%", md: "auto" } }}>
            <Button
              onClick={handleClose}
              sx={{
                py: 1.25,
                px: 2.5,
                textTransform: "none",
                fontWeight: 600,
                color: "#64748b",
                borderRadius: 1.5,
                border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                "&:hover": { bgcolor: alpha("#000", 0.04) },
                flexShrink: 0,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<ShoppingCart sx={{ fontSize: "1rem !important" }} />}
              onClick={handleAddToCart}
              disabled={isAddToCartDisabled}
              fullWidth={isMobile}
              sx={{
                py: 1.25,
                px: { xs: 2.5, md: 3.5 },
                textTransform: "none",
                fontWeight: 800,
                fontSize: "0.95rem",
                borderRadius: 1.5,
                flexGrow: { xs: 1, md: 0 },
                background: chrome.gradient,
                boxShadow: chrome.shadow,
                "&:hover": {
                  background: chrome.gradientHover,
                  boxShadow: chrome.shadowHover,
                },
                "&:disabled": {
                  bgcolor: alpha("#000", 0.1),
                  color: alpha("#000", 0.26),
                  boxShadow: "none",
                },
              }}
            >
              Add to Order
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={postAddChoiceOpen}
        onClose={() => setPostAddChoiceOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2.5,
            border: isMobile
              ? "none"
              : `1px solid ${alpha(theme.palette.divider, 0.4)}`,
            overflow: "hidden",
            boxShadow: isMobile ? "none" : "0 20px 44px rgba(15,23,42,0.22)",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(7, 14, 28, 0.46)",
            backdropFilter: "blur(6px)",
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            py: { xs: 2, md: 2.25 },
            background: luxury ? LUXURY.barGradient : chrome.gradient,
            color: "#ffffff",
            position: "relative",
          }}
        >
          <IconButton
            onClick={() => setPostAddChoiceOpen(false)}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              color: "#ffffff",
              bgcolor: alpha("#ffffff", 0.14),
              "&:hover": { bgcolor: alpha("#ffffff", 0.22) },
            }}
          >
            <Close fontSize="small" />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, pr: 4 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: alpha("#ffffff", 0.18),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckCircle sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                Added to your order
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", color: alpha("#ffffff", 0.88) }}>
                What would you like to do next?
              </Typography>
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ p: { xs: 2, md: 2.5 }, background: "#f8fafc" }}>
          <Stack spacing={1.2}>
            <Button
              onClick={handleAddAnotherVariation}
              fullWidth
              sx={{
                justifyContent: "space-between",
                textTransform: "none",
                borderRadius: 2,
                p: 1.4,
                bgcolor: "#ffffff",
                border: `1px solid ${alpha(theme.palette.divider, 0.45)}`,
                color: "#0f172a",
                "&:hover": { bgcolor: "#f8faff", borderColor: alpha(accent, 0.5) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.2,
                    bgcolor: alpha(accent, 0.12),
                    color: accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Tune sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ textAlign: "left" }}>
                  <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    Add another variation
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>
                    Keep customizing this same item
                  </Typography>
                </Box>
              </Box>
              <ArrowForward sx={{ fontSize: 18, color: "#94a3b8" }} />
            </Button>

            <Button
              onClick={handleAddNewItem}
              fullWidth
              sx={{
                justifyContent: "space-between",
                textTransform: "none",
                borderRadius: 2,
                p: 1.4,
                bgcolor: "#ffffff",
                border: `1px solid ${alpha(theme.palette.divider, 0.45)}`,
                color: "#0f172a",
                "&:hover": { bgcolor: "#f8faff", borderColor: alpha(accent, 0.5) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.2,
                    bgcolor: alpha(accent, 0.12),
                    color: accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <RestaurantMenu sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ textAlign: "left" }}>
                  <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    Add a new item
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>
                    Return to menu and pick something else
                  </Typography>
                </Box>
              </Box>
              <ArrowForward sx={{ fontSize: 18, color: "#94a3b8" }} />
            </Button>

            <Button
              onClick={handleCompleteOrder}
              fullWidth
              variant="contained"
              sx={{
                justifyContent: "space-between",
                textTransform: "none",
                borderRadius: 2,
                p: 1.4,
                background: chrome.gradient,
                boxShadow: chrome.shadow,
                "&:hover": {
                  background: chrome.gradientHover,
                  boxShadow: chrome.shadowHover,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.2,
                    bgcolor: alpha("#ffffff", 0.2),
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ShoppingCart sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ textAlign: "left" }}>
                  <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                    Complete order
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: alpha("#ffffff", 0.9) }}>
                    Go to checkout and finish this order
                  </Typography>
                </Box>
              </Box>
              <ArrowForward sx={{ fontSize: 18, color: "#ffffff" }} />
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddToCartDialog;
